import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { getCurrentUser, logout as apiLogout, pingPresence } from '../services/api';
import { canAccessPage } from '../utils/pageAccess';

const PRESENCE_PING_INTERVAL = 30000;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  // Режим просмотра организации супер-админом (только чтение) — null, когда неактивен
  const [impersonation, setImpersonation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

const checkAuth = async () => {
  try {
    const data = await getCurrentUser();
    if (data.authenticated) {
      // Отключённые платформой разделы — рядом с правами роли: их читают
      // те же canAccessPage/homePath.
      setUser({ ...data.user, disabled_modules: data.disabled_modules || {} });
      setOrganization(data.organization || null);
      setIsPlatformAdmin(!!data.is_platform_admin);
      setImpersonation(data.impersonation || null);
      setIsAuthenticated(true);
    } else {
      setUser(null);
      setOrganization(null);
      setIsPlatformAdmin(false);
      setImpersonation(null);
      setIsAuthenticated(false);
    }
  } catch (error) {
    console.error('Auth error:', error);
    setUser(null);
    setOrganization(null);
    setIsPlatformAdmin(false);
    setImpersonation(null);
    setIsAuthenticated(false);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    checkAuth();
  }, []);

  const presenceIntervalRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated) {
      pingPresence().catch(() => {});
      presenceIntervalRef.current = setInterval(() => {
        pingPresence().catch(() => {});
      }, PRESENCE_PING_INTERVAL);
    }
    return () => {
      if (presenceIntervalRef.current) clearInterval(presenceIntervalRef.current);
    };
  }, [isAuthenticated]);

  const logout = async () => {
    await apiLogout();
    setUser(null);
    setOrganization(null);
    setIsPlatformAdmin(false);
    setImpersonation(null);
    setIsAuthenticated(false);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{
      user, organization, isPlatformAdmin, impersonation, loading, isAuthenticated, logout, checkAuth,
      canAccess: (page) => canAccessPage(user, page),
      moduleBlock: (page) => user?.disabled_modules?.[page] || null,
    }}>
      {children}
    </AuthContext.Provider>
  );
};